import React, { useCallback, useEffect, useState, ReactElement } from 'react';

import {
  IdvStateRequest,
  IdvVariables,
  Brands,
  IdvRoutes,
  IdvStatus,
  IdvErrors,
} from '@cbbt/lui-idv';
import { Spinner } from '@constellation/core';
import { useContent } from '@interstellar/react-app-content';
import { useNavigate } from '@interstellar/react-app-routing';

import { IdvWrapperContent } from './IdvWrapperContent';
import IdvPreviousDialog from '../../components/dialog/IdvPreviousDialog';
import { useGlobalProvider } from '../../context/GlobalContext';
import { getConfigValue, getJwtTokenCallback } from '../../utils/configUtils';
import {
  APP_REFERENCE_ID,
  CONFIG_KEYS,
  DOC_CATEGORY,
  IA_LAB_HEADERS_BOS,
  IA_LAB_HEADERS_LTB,
  IDV_SUBMIT_STATUS,
  MITEK_LICENCE,
} from '../../utils/constant';

function IdvWrapper(): ReactElement {
  const navigate = useNavigate();
  const { globalState, setGlobalContext } = useGlobalProvider();
  const { idvWrapperContent } = useContent<IdvWrapperContent>();
  const {
    serviceName,
    contactNo,
    loaderMessage,
    submitRegistrationPath,
    hardFailurePath,
    threeStepsToRegisterIdVerifyPath,
    dialogTitle,
    dialogContent,
    noBtnLabel,
    yesBtnLabel,
    yesBtnLink,
    idvStartPath,
    idvErrorPath,
    technicalErrorHref,
  } = idvWrapperContent?.[0] || {};

  const [identityLuiVariables, setIdentityLuiVariables] =
    useState<IdvVariables | null>(null);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const useIdvStateHandler = useCallback(
    async (idvStateRequest: IdvStateRequest): Promise<void> => {
      console.log('idvStateRequest', idvStateRequest);
      const { idvRetryCount } = globalState;

      switch (idvStateRequest.status) {
        case IdvStatus.PREVIOUS:
          console.log('IdvStatus.PREVIOUS', IdvStatus.PREVIOUS);
          const currentUrl = window.location.href;
          if (currentUrl.includes(idvStartPath)) {
            setIsDialogOpen(true);
          } else {
            navigate(idvStartPath);
          }
          break;
        case IdvStatus.SKIP:
          console.log('IdvStatus.SKIP', IdvStatus.SKIP);
          navigate(threeStepsToRegisterIdVerifyPath);
          break;
        case IdvStatus.ERROR:
          console.log('IdvStatus.ERROR', IdvStatus.ERROR);
          if (
            idvStateRequest.body.error instanceof IdvErrors.IdvVerificationError
          ) {
            console.log(
              'IdvErrors.IdvVerificationError',
              IdvErrors.IdvVerificationError,
            );
            if (idvRetryCount < 2) {
              setGlobalContext({
                idvRetryCount: idvRetryCount + 1,
              });
            } else {
              navigate(hardFailurePath);
            }
          } else if (
            idvStateRequest.body.error instanceof
            IdvErrors.IdvTokenFailureCaptureSelfie
          ) {
            console.log(
              'IdvErrors.IdvTokenFailureCaptureSelfie',
              IdvErrors.IdvTokenFailureCaptureSelfie,
            );
            navigate(idvErrorPath);
          } else if (
            idvStateRequest.body.error instanceof IdvErrors.IdvSubmitFailure
          ) {
            console.log(
              'IdvErrors.IdvSubmitFailure',
              IdvErrors.IdvSubmitFailure,
            );
            navigate(idvErrorPath);
          } else {
            navigate(technicalErrorHref);
          }
          break;
        case IdvStatus.SUBMIT:
          console.log('IdvStatus.SUBMIT', IdvStatus.SUBMIT);
          const { outcome, caseDetails } = idvStateRequest?.body?.idvSubmitData;
          const { passed, passToAgent } = IDV_SUBMIT_STATUS;
          if (outcome === passed || outcome === passToAgent) {
            console.log('outcome === passToAgent', outcome === passToAgent);
            console.log('outcome === passed', outcome === passed);
            const documents =
              caseDetails?.partyDetails[0]?.attachmentDetails?.map(
                (detail) => ({
                  name: detail?.fileName,
                  category: DOC_CATEGORY,
                  id: detail?.tmpSysFileRefNum?.replace(/[{}]/g, ''),
                }),
              ) || [];
            setGlobalContext({
              customerDetails: {
                ...globalState.customerDetails,
                documents,
              },
            });
            navigate(submitRegistrationPath);
          } else {
            navigate(technicalErrorHref);
          }
          break;
        default:
          break;
      }
    },
    [
      navigate,
      submitRegistrationPath,
      technicalErrorHref,
      globalState,
      setGlobalContext,
    ],
  );

  const fetchIndividualDetails = useCallback(async () => {
    const { customerDetails } = globalState || {};
    setIdentityLuiVariables({
      state: {
        applicationId: APP_REFERENCE_ID,
        applicationRefNumber: '',
        brand:
          (window as any).appConfig.BRAND_NAME === 'bos'
            ? Brands.BOS
            : Brands.LTB,
        maxRetries: 3,
        individualData: {
          individualId: customerDetails?.individual?.partyId,
          processCode: '',
          dossierId: '',
          retryCount: 0,
          personalDetails: {
            firstname: customerDetails?.individual?.firstName,
            middlename: '',
            surname: customerDetails?.individual?.lastName,
            dateOfBirth: customerDetails?.individual?.dateOfBirth,
            mobileNumber: customerDetails?.individual?.telephone,
          },
          addresses: {},
          nationalities: {
            countryCode: '',
            nationality: '',
          },
          kycResult: [],
        },
      },
      apis: {
        idvUploadLabUrl: getConfigValue(CONFIG_KEYS.idvApiHostPath),
        docUploadLabUrl: getConfigValue(CONFIG_KEYS.docApiHostPath),
        svcIdvBasePath: getConfigValue(CONFIG_KEYS.svcIdvApiHostPath),
      },
      callbackMethods: {
        idvStateHandler: useIdvStateHandler,
        getJwt: getJwtTokenCallback,
      },
      apimSubscriptionKey: getConfigValue(CONFIG_KEYS.idvSubscriptionKey),
      assetsPath: new URL(
        `${(window as any).appConfig.BASE_PATH}public/assets/idv`,
        window.location.origin,
      ).toString(),
      mitekLicenseKey: MITEK_LICENCE,
      labels: {
        applicationHeading: serviceName,
        helpDeskContact: contactNo,
      },
      iaLabHeaders:
        (window as any).appConfig.BRAND_NAME === 'bos'
          ? IA_LAB_HEADERS_BOS
          : IA_LAB_HEADERS_LTB,
      identityConfirmationByPost: true,
    });
    setLoading(false);
  }, [useIdvStateHandler, contactNo, globalState, serviceName]);

  useEffect(() => {
    fetchIndividualDetails();
  }, [fetchIndividualDetails]);

  return (
    <>
      {loading && <Spinner message={loaderMessage} />}
      <IdvPreviousDialog
        dialogTitle={dialogTitle}
        dialogContent={dialogContent}
        isDialogOpen={isDialogOpen}
        setIsDialogOpen={setIsDialogOpen}
        noBtnLabel={noBtnLabel}
        yesBtnLabel={yesBtnLabel}
        yesBtnLink={yesBtnLink}
      />
      {identityLuiVariables && (
        <IdvRoutes idvVariables={identityLuiVariables} />
      )}
    </>
  );
}

export default IdvWrapper;
